import * as React from 'react';
import { ProtectedRoutes, RouteGroups, URLS } from '../router-x/routes';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Chip, Drawer, IconButton, List, ListItem, Menu, MenuHandler, MenuItem, MenuList } from '@material-tailwind/react';
// import LOGO from '../assets/workbench_logo.png';
import { ArrowUpRightIcon, ChevronRightIcon, LogOutIcon, MenuIcon, PanelLeftCloseIcon } from 'lucide-react';
// SignOut is handled via authContext logout() to ensure sessionStorage is cleared
import { useAuth } from '../services/contexts/authContext';
import PulseLogo from '../assets/svg-pulse-logo';
import { convertToPresentableText } from '../utils/functions';
import { useGetUserNotifications } from '../custom-hooks/use-notifications';
import { STATIC_USER_ROLES } from '../utils/enums';
import ImpersonationBanner from './impersonation-banner';
import PortalShell from './portal-shell';
// const drawerWidth = 240;

const ParentContainer = (props) => {
	const { children } = props;
	const location = useLocation();
	const navigate = useNavigate();
	// const [mobileOpen, setMobileOpen] = React.useState(false);
	// const [isClosing, setIsClosing] = React.useState(false);
	const [open, setOpen] = React.useState(false);

	const openDrawer = () => setOpen(true);
	const closeDrawer = () => setOpen(false);

	const mainScrollRef = React.useRef(null);
	const scrollSaveFrame = React.useRef(null);

	// Restore this page's scroll position on mount (the sidebar layout scrolls this
	// inner container, not window, so browser/router scroll restoration can't reach it)
	React.useLayoutEffect(() => {
		const container = mainScrollRef.current;
		if (!container) return;
		const savedScrollTop = sessionStorage.getItem(`page-scroll-${location.pathname}`);
		container.scrollTop = savedScrollTop ? parseInt(savedScrollTop, 10) : 0;
	}, [location.pathname]);

	React.useEffect(() => () => {
		if (scrollSaveFrame.current) cancelAnimationFrame(scrollSaveFrame.current);
	}, []);

	// Coalesce rapid scroll events to one write per frame instead of one per event
	const handleMainScroll = (e) => {
		const scrollTop = e.currentTarget.scrollTop;
		if (scrollSaveFrame.current) cancelAnimationFrame(scrollSaveFrame.current);
		scrollSaveFrame.current = requestAnimationFrame(() => {
			sessionStorage.setItem(`page-scroll-${location.pathname}`, scrollTop.toString());
		});
	};

	const { userDetails, logout } = useAuth();

	const handleLogout = async () => {
		try {
			const result = await logout();
			if (result?.wasImpersonating) {
				// Return admin to the customer detail page they came from
				navigate(result.returnPath || URLS.ADMIN.ROOT);
			}
			// Normal logout — onAuthStateChanged will redirect to /auth
		} catch (err) {
			console.error('Logout failed:', err);
		}
	};

	const { data: notificationsData } = useGetUserNotifications();
	const userNotificationsCount = React.useMemo(() => {
		const list = notificationsData?.notifications || [];
		return list.filter(n => !n.readAt).length;
	}, [notificationsData]);

	// Customer & team-member roles use the redesigned Pulse portal shell.
	// All other roles (admin, staff, receptionist, support-agent) keep the existing layout below.
	if ([STATIC_USER_ROLES.USER, STATIC_USER_ROLES.TEAM_MEMBER].includes(userDetails?.role)) {
		return <PortalShell title={props.title}>{children}</PortalShell>;
	}


	// const handleDrawerClose = () => {
	//   setIsClosing(true);
	//   setMobileOpen(false);
	// };

	// const handleDrawerTransitionEnd = () => {
	//   setIsClosing(false);
	// };

	// const handleDrawerToggle = () => {
	//   if (!isClosing) {
	//     setMobileOpen(!mobileOpen);
	//   }
	// };

	const groupEntries = Object.entries(RouteGroups);
	// Dynamic RBAC: helper to check if a user has access to a route
	const hasRouteAccess = (route) => {
		if (!route.inSideBarList) return false;
		// Super admin sees everything
		if (userDetails.role === 'super_admin') return true;
		// Staff role — check dynamic permissions
		if (userDetails.role === 'staff') {
			const permissions = userDetails?.dynamicRole?.permissions || [];
			return permissions.includes(route.id);
		}
		// Legacy/customer roles — use static permissibleRoles
		let hasRoleAccess = route.permissibleRoles.includes(userDetails.role);
		
		// Apply allowed pages logic if requiredAllowedPage is specified
		if (hasRoleAccess && route.requiredAllowedPage) {
			const userAllowedPages = userDetails?.allowedPages || [];
			hasRoleAccess = userAllowedPages.includes(route.requiredAllowedPage);
		}
		
		return hasRoleAccess;
	};

	const allPermissibleRoutes = ProtectedRoutes.filter(item => hasRouteAccess(item));
	const groups = {};
	for (let i = 0; i < groupEntries.length; ++i) {
		const [key, value] = groupEntries[i];
		const routes = allPermissibleRoutes.filter(item => item.group === value); // routes that are in the group	
		if (routes.length > 0) {
			groups[key] = routes;
		}
	}


	const drawer = (
		<div>
			<div className='flex flex-col items-center justify-center mt-3'>
				<div className="text-lg font-thin text-center px-5 flex justify-center items-end gap-2">
					<PulseLogo />
					<span className='text-primary text-2xl font-normal mb-2'>Pulse</span>
				</div>
				<span className='text-orange-900 text-sm font-thin'>Project of Workbench</span>
				{/* <span className='text-gray-500 text-xs font-thin'>Powered by CFT</span> */}
			</div>
			<hr className='my-5' />
			<List className='min-w-full'>
				{allPermissibleRoutes.filter(item => item.group === null).map((route, index) => (
					<Link key={index} to={route.path} onClick={closeDrawer} className='outline-none focus:outline-none'>
						<ListItem className='rounded-full' selected={location.pathname === route.path}>
							<div className='flex items-center justify-between w-full'>
								<div className="flex items-center">
									<div className="pr-3">
										{route.icon}
									</div>
									<div className="text-sm font-thin flex">
										{route.name}
										{
											['FX7-1', 'FX18-2', 'FX20-1', 'FX25'].includes(route.id) && userNotificationsCount > 0 &&
											<Chip
												size='sm'
												variant='ghost'
												color='orange'
												value={userNotificationsCount}
												className='ml-2 px-2 py-0 rounded-full' />
										}
									</div>
								</div>
								<div>{route.secondaryIcon}</div>
							</div>
						</ListItem>
					</Link>
				))}
				<hr className='my-2' />
				<div className='hidden md:block'>
					{
						Object.entries(groups).map(([key, value]) => (
							<Menu placement='right-start' key={key}>
								<MenuHandler>
									<ListItem className='rounded-full outline-none focus:outline-none focus:ring-0 active:outline-none' >
										<div className="flex items-center justify-between w-full">
											<div className="flex items-center gap-2">
												<div>
													<ArrowUpRightIcon size={16} />
												</div>
												<div className='text-sm font-thin'>
													{convertToPresentableText(key.toLowerCase())}
												</div>
											</div>
											<div className="text-sm font-thin">
												<ChevronRightIcon size={16} />
											</div>
										</div>
									</ListItem>
								</MenuHandler>
								<MenuList>
									{
										value.filter(item => hasRouteAccess(item)).map((route, index) => (
											<Link key={index} to={route.path} className='outline-none focus:outline-none'>
												<MenuItem className='rounded-lg border-0 outline-none ring-0 transition-colors duration-150 hover:bg-blue-gray-50 focus:bg-blue-gray-50 focus:outline-none focus:ring-0 active:outline-none active:ring-0 active:border-0'>
													<div className='flex items-center gap-2'>
														<div className='text-sm font-thin'>
															{route.icon}
														</div>
														<div className='text-sm font-thin'>
															{route.name}
														</div>
													</div>
												</MenuItem>
											</Link>
										))
									}
								</MenuList>
							</Menu>
						))
					}
				</div>
				<div className='block md:hidden'>
					{
						Object.entries(groups).map(([key, value]) => (
							<Menu placement='bottom' key={key}>
								<MenuHandler>
									<ListItem className='rounded-full outline-none focus:outline-none focus:ring-0 active:outline-none' >
										<div className="flex items-center justify-between w-full">
											<div className="flex items-center gap-2">
												<div>
													<ArrowUpRightIcon size={16} />
												</div>
												<div className='text-sm font-thin'>
													{convertToPresentableText(key.toLowerCase())}
												</div>
											</div>
											<div className="text-sm font-thin">
												<ChevronRightIcon size={16} />
											</div>
										</div>
									</ListItem>
								</MenuHandler>
								<MenuList className='z-[10000]'>
									{
										value.map((route, index) => (
											<Link key={index} to={route.path} className='outline-none focus:outline-none'>
												<MenuItem onClick={closeDrawer} className='rounded-lg border-0 outline-none ring-0 transition-colors duration-150 hover:bg-blue-gray-50 focus:bg-blue-gray-50 focus:outline-none focus:ring-0 active:outline-none active:ring-0 active:border-0'>
													<div className='flex items-center gap-2'>
														<div className='text-sm font-thin'>
															{route.icon}
														</div>
														<div className='text-sm font-thin'>
															{route.name}
														</div>
													</div>
												</MenuItem>
											</Link>
										))
									}
								</MenuList>
							</Menu>
						))
					}
				</div>
				<hr className='my-2' />
				<ListItem className='rounded-full' onClick={handleLogout}>
					<div className="flex items-center">
						<div className="pr-3">
							<LogOutIcon size={16} />
						</div>
						<div className="text-sm font-thin">
							Logout
						</div>
					</div>
				</ListItem>
			</List>
		</div>
	);


	return (
		<React.Fragment>
			<ImpersonationBanner />
			<div className="flex">
				<div className="w-[250px] h-screen overflow-y-auto border-r hidden md:block z-10">
					{drawer}
				</div>
				<div ref={mainScrollRef} onScroll={handleMainScroll} className="w-full md:w-[calc(100%-250px)] h-screen overflow-y-auto">
					<div className={`h-[120px] p-5 w-full md:hidden`}>
						<div className={`rounded-3xl border p-5 flex justify-between items-center`}>
							{/* <img src={LOGO} alt='logo' className='max-h-[50px] max-w-[170px]' /> */}
							<div className='flex items-center gap-2'>
								<PulseLogo height={40} width={40} />
								<span className='text-primary text-2xl font-normal'>Pulse</span>
							</div>
							<IconButton className='rounded-full' size='sm' onClick={openDrawer}>
								<MenuIcon size={12} />
							</IconButton>
						</div>
					</div>
					{children}
				</div>
			</div>
			<Drawer open={open} onClose={closeDrawer}>
				<div className={``}>
					<div className={`h-screen overflow-y-auto`}>
						{drawer}
					</div>
					<div className={`h-screen overflow-y-auto`}>
						<hr />
						<div className={`flex justify-end`}>
							<IconButton className='rounded-full' variant='text' size='lg' onClick={closeDrawer}>
								<PanelLeftCloseIcon size={24} />
							</IconButton>
						</div>
					</div>
				</div>
			</Drawer>
		</React.Fragment>
	);
}

export default ParentContainer;
